import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    signal,
} from '@angular/core';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatOption, MatSelect } from '@angular/material/select';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { debounceTime, distinctUntilChanged, finalize, forkJoin, Observable, Subscription, switchMap, tap } from 'rxjs';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { CdkFixedSizeVirtualScroll, CdkVirtualForOf, CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { ICommonDropdownService } from './typing/common-dropdown-service.interface';
import { MatTooltip } from '@angular/material/tooltip';
import { ILabelValue } from './typing/label-value.interface';
import { LoaderComponent } from '../loader/loader.component';
import { Params } from '@angular/router';
import { MessageService } from '../../services/message.service';
import { IPaginatedResponse } from '../../typing/response.interface';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'app-common-dropdown',
    standalone: true,
    imports: [
        MatFormField,
        MatSelect,
        ReactiveFormsModule,
        MatOption,
        MatLabel,
        NgxMatSelectSearchModule,
        CdkFixedSizeVirtualScroll,
        CdkVirtualForOf,
        CdkVirtualScrollViewport,
        MatTooltip,
        FormsModule,
        LoaderComponent,
    ],
    templateUrl: './common-dropdown.component.html',
    styleUrl: './common-dropdown.component.scss',
})
export class CommonDropdownComponent implements OnInit, OnDestroy {
    @Input({ required: true }) formCtrl!: FormControl;
    @Input() multiple = false;
    @Input({ required: true }) label!: string;
    @Input({ required: true }) service!: ICommonDropdownService;
    @Input() noneOption = true;
    @Input() tooltipEnabled = false;
    @Input() disabled = false;
    @Input() searchBy? = '';
    @Input() selectAllButton = false;

    @Output() onSelectOption = new EventEmitter(); // этот эмит нужен для обновления фильтров в advanced search

    searchControl = new FormControl();
    options = signal<ILabelValue[]>([]);
    loading = signal(false);
    page = 0;
    totalPages = 0;
    private subscriptions: Subscription[] = [];
    selectAllValue = undefined; // Специальное значение, не совпадающее с реальными value
    selectedAll = false;

    constructor(
        private messageService: MessageService,
    ) {}

    ngOnInit(): void {
        // Первичная проверка, есть ли значение форм контрола в списке
        this.updateOptions();

        // Подписка на изменения formCtrl
        const formCtrlSub = this.formCtrl.valueChanges.subscribe(() => {
            this.updateOptions();
        });
        this.subscriptions.push(formCtrlSub);

        const searchControlSub = this.searchControl.valueChanges
            .pipe(
                debounceTime(300),
                distinctUntilChanged(),
                switchMap(value => {
                    this.page = 0;
                    return this.loadOptions(value);
                }),
            )
            .subscribe(options => {
                const selectedOption = !this.multiple
                    ? this.options().find(option => option.value === this.formCtrl.value)
                    : Array.isArray(this.formCtrl.value)
                        ? this.options().filter(option => this.formCtrl.value.some((item: number) => item === option.value))
                        : [];
                this.options.set(
                    selectedOption ? (!this.multiple ? [selectedOption as ILabelValue] : (selectedOption as ILabelValue[])) : [],
                );
                this.addUniqueOptions(options.data);
                // это для того чтобы не удалялся активный опшен при поиске новых
                // и при закрытии списка срабатывает эта подписка, чтобы не удалялся активный опшен
                this.loading.set(false);
            });
        this.subscriptions.push(searchControlSub);
        if (this.disabled) {
            this.formCtrl.disable();
        }
    }

    ngOnDestroy(): void {
        // отписаться при выходе из компонента
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }

    onScroll(index: number): void {
        if (this.loading() || this.page > this.totalPages) {
            return;
        }

        const end = this.options().length;
        if (index >= end - 5) {
            this.loadOptions(this.searchControl.value, this.page)
                .pipe(debounceTime(300))
                .subscribe(options => {
                    this.addUniqueOptions(options.data);
                    this.page++;
                });
        }
    }

    loadOptions(search?: string, page?: number): Observable<IPaginatedResponse<ILabelValue>> {
        this.loading.set(true);
        const queryParams: Params = this.searchBy !== '' ? {
                search: search || '',
                limit: 20,
                page: page || 1,
                searchBy: [this.searchBy],
            } :
            {
                search: search || '',
                limit: 20,
                page: page || 1,
            };

        return this.service.getList(queryParams).pipe(
            tap((res: IPaginatedResponse<any>) => {
                this.totalPages = res.meta?.totalPages || 1;}),
            finalize(() => this.loading.set(false))
        );
    }

    ensureSelectedItemsInOptions(selectedItems: number | string | number[] | string[]): Observable<ILabelValue>[] {
        let items: (number | string)[];
        // работаем с массивом значений
        if (!Array.isArray(selectedItems)) {
            items = [selectedItems];
        } else {
            items = selectedItems;
        }

        const missingItems = items.filter(selectedItem => !this.options().some(option => option.value === selectedItem))
            .filter(item => item !== undefined);

        return missingItems.map(item => this.service.getOptionsByValue(item));
    }

    onOpenedChange(isOpened: boolean): void {
        if (isOpened) {
            this.page = 1;
            this.totalPages = 0;

            // Получаем текущее выбранное значение
            const selectedValue = this.formCtrl.value;
            let selectedOptions: ILabelValue[] = [];

            if (selectedValue && this.options().length > 0) {
                if (this.multiple && Array.isArray(selectedValue)) {
                    selectedOptions = this.options().filter(opt => selectedValue.includes(opt.value));
                } else if (!this.multiple) {
                    const singleSelected = this.options().find(opt => opt.value === selectedValue);
                    if (singleSelected) {
                        selectedOptions = [singleSelected];
                    }
                }
            }

            // При открытии помещаем выбранные элементы в начало списка
            this.options.set(selectedOptions);
            //при открытии списка получаем опшены
            this.loadOptions().subscribe(options => {
                this.addUniqueOptions(options.data);
            });
        }
    }

    updateOptions(): void {
        if (!this.formCtrl.value) {
            return;
        }
        this.loading.set(true);
        forkJoin(this.ensureSelectedItemsInOptions(this.formCtrl.value))
            .subscribe({
                next: value => this.prependUniqueOptions(value),
                error: () => this.messageService.errorMessage('Произошла ошибка при загрузке списка'),
            })
            .add(() => this.loading.set(false));
    }

    selectedValue(value: string | number | number[] | null): void {
        // этот метод нужен для обновления фильтров в advanced search
        this.onSelectOption.emit(value);
    }

    private addUniqueOptions(newOptions: ILabelValue[]): void {
        this.options.update(currentOptions => {
            const existingValues = new Set(currentOptions.map(option => option.value));
            const filteredOptions = newOptions.filter(option => !existingValues.has(option.value));
            return [...currentOptions, ...filteredOptions];
        });
    }

    private prependUniqueOptions(newOptions: ILabelValue[]): void {
        this.options.update(currentOptions => {
            const newValues = new Set(newOptions.map(option => option.value));
            const restOptions = currentOptions.filter(option => !newValues.has(option.value));
            return [...newOptions, ...restOptions];
        });
    }

    toggleSelectAll(): void {
        const allValues = this.options().map(o => o.value);
        if (this.selectedAll) {
            this.formCtrl.setValue([]);
            this.onSelectOption.emit([]);
            this.selectedAll = false;
        } else {
            this.formCtrl.setValue(allValues);
            this.onSelectOption.emit(allValues);
            this.selectedAll = true;
        }
    }

}
